// Centralized React Query keys for the Breakfast feature.
//
// Why a dedicated module:
//   • Every mutation in this feature invalidates `["breakfastEvents"]` by
//     prefix and (for invite-touching mutations) `["pendingBreakfastInvites"]`.
//     A typo in the string (e.g. snake_case `breakfast_events`) silently
//     matches nothing — the mutation's `onSuccess` runs, the UI's success
//     toast fires, but the cache stays stale. That's the "fake success" bug
//     we repeatedly hit before consolidating keys here.
//   • Having a single source of truth makes it trivial to rename the keys
//     later without grepping every hook file.
//
// Cross-feature invalidation (e.g. `circles_schedule`, `trackedDays`,
// `user-attendance`, `eventDetails`) is intentionally NOT listed here — those
// keys belong to other features and are referenced as string literals by the
// mutations below, marked with `// cross-feature:` comments at the call site.

export const BREAKFAST_EVENTS_ROOT = "breakfastEvents" as const;
export const PENDING_BREAKFAST_INVITES_ROOT = "pendingBreakfastInvites" as const;

/** Prefix key — pass to `invalidateQueries` / `getQueriesData` for fan-out. */
export const breakfastEventsPrefix = [BREAKFAST_EVENTS_ROOT] as const;

/** Prefix key for the RSVP Center pending-invites cache. */
export const pendingInvitesPrefix = [PENDING_BREAKFAST_INVITES_ROOT] as const;

/** Full query key for a specific `useBreakfastEvents` call. */
export function breakfastEventsKey(
  userId: string | undefined,
  startDate: string,
  endDate: string,
) {
  return [BREAKFAST_EVENTS_ROOT, userId, startDate, endDate] as const;
}

/** Full query key for a specific `usePendingBreakfastInvites` call. */
export function pendingInvitesKey(userId: string | undefined) {
  return [PENDING_BREAKFAST_INVITES_ROOT, userId] as const;
}
